import {effect, Injectable, signal} from '@angular/core'
import {STORAGE_KEYS, StorageService} from '@/services/storage.service'
import {MARKET_OUTCOMES, pairKey} from './demo-offer'
import {LiabilityLimit} from './mock-betslip-risk-backend'

/** Which column of the Liability tab prices the player's betslip, if any. */
export enum PlayerLiabilityTier {
	normal = 'normal',
	vip = 'vip',
	off = 'off'
}

/** One Liability Limit per market-outcome pair — the demo API's defaults. */
export function defaultLiabilityLimits(): LiabilityLimit[] {
	return MARKET_OUTCOMES.map((pair) => ({...pair, liabilityLimit: 500, vipLiabilityLimit: 2000}))
}

/**
 * The workbench's Liability Limits and the player both betslips run for.
 *
 * Root rather than on the workbench, so they outlive the overlay and the player's betslip mock
 * (`PlayerLiabilityMock`) prices off the same table the workbench edits. Kept across reloads in
 * localStorage, where only what differs from the defaults is stored: `reset` brings back the
 * defaults and leaves nothing stored, and a default changed in code still reaches what was not edited.
 */
@Injectable({providedIn: 'root'})
export class LiabilityLimitsState {
	readonly limits = signal<LiabilityLimit[]>(loadLiabilityLimits())

	/**
	 * The player both betslips run for. A normal player is accepted automatically up to the Limit
	 * column and capped at the VIP column; a VIP up to the VIP column and not capped; a stake above
	 * the Auto-accept limit goes for Authorization. `off` leaves the main betslip to the site's own
	 * limits and risk info.
	 */
	readonly playerTier = signal(StorageService.getOrDefault(STORAGE_KEYS.devPlayerTier) as PlayerLiabilityTier)

	constructor() {
		effect(() => {
			const changed = changedLiabilityLimits(this.limits())
			if (changed.length === 0) {
				StorageService.remove(STORAGE_KEYS.devLiabilityLimits)
			} else {
				StorageService.set(STORAGE_KEYS.devLiabilityLimits, changed)
			}
		})

		effect(() => {
			const tier = this.playerTier()
			if (tier === PlayerLiabilityTier.normal) {
				StorageService.remove(STORAGE_KEYS.devPlayerTier)
			} else {
				StorageService.set(STORAGE_KEYS.devPlayerTier, tier as `${PlayerLiabilityTier}`)
			}
		})
	}

	/** Back to the default limits and a normal player, with nothing stored. */
	reset(): void {
		this.limits.set(defaultLiabilityLimits())
		this.playerTier.set(PlayerLiabilityTier.normal)
	}
}

/** Saved limits laid over the defaults pair by pair, so a pair the table gained since keeps its default. */
function loadLiabilityLimits(): LiabilityLimit[] {
	const saved = new Map((StorageService.get(STORAGE_KEYS.devLiabilityLimits) ?? [])
		.map((limit) => [pairKey(limit), limit]))

	return defaultLiabilityLimits().map((limit) => {
		const stored = saved.get(pairKey(limit))
		return stored
			? {...limit, liabilityLimit: stored.liabilityLimit, vipLiabilityLimit: stored.vipLiabilityLimit}
			: limit
	})
}

/** A pair's limits as stored: the pair and its two limits, without the labels. */
export type StoredLiabilityLimit = Pick<LiabilityLimit, 'marketId' | 'outcomeKey' | 'liabilityLimit' | 'vipLiabilityLimit'>

/** The pairs whose limits differ from the defaults, in the shape they are stored in. */
function changedLiabilityLimits(limits: LiabilityLimit[]): StoredLiabilityLimit[] {
	const defaults = new Map(defaultLiabilityLimits().map((limit) => [pairKey(limit), limit]))

	return limits
		.filter((limit) => {
			const fallback = defaults.get(pairKey(limit))
			return limit.liabilityLimit !== fallback?.liabilityLimit
				|| limit.vipLiabilityLimit !== fallback?.vipLiabilityLimit
		})
		.map(({marketId, outcomeKey, liabilityLimit, vipLiabilityLimit}) =>
			({marketId, outcomeKey, liabilityLimit, vipLiabilityLimit}))
}
